import React, { useCallback, useEffect, useState } from "react"
import { useRouter } from "next/router"
import { InferGetServerSidePropsType, type GetStaticProps } from "next"
import Sticky from "react-stickynode"
import styled from "styled-components"
import { useTranslation } from "next-i18next"
import dynamic from "next/dynamic"
import axiosRetry from "axios-retry"
import axios from "axios"
import Pagination from "@mui/material/Pagination"
import SectionLine from "@/common/components/_UI/SectionLine"
import SectionHeading from "@/common/components/SectionHeading"

import ResetCSS from "@/common/assets/css/style"
import MetaData from "@/common/components/MetaData"
import NavbarNew from "@/containers/common/NavbarNew"
import { theme, section } from "@/common/Mixin"
import { ContentWrapper } from "@/containers/common/style"
import BlogCard from "@/common/components/BlogCard"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { getBlogs } from "@/helpers/getBlogs"

const Footer = dynamic(() => import("@/containers/common/FooterNew"))
const BlogMenu = dynamic(() => import("@/containers/Blog/BlogMenu"))
const Search = dynamic(() => import("@/containers/Blog/Search"))

axiosRetry(axios, { retries: 3 })

export const DEFAULT_PAGE_SIZE = 12
const DEFAULT_PAGE_NUMBER = 1

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const { data, page, pageSize } = await getBlogs(
    locale,
    DEFAULT_PAGE_SIZE,
    DEFAULT_PAGE_NUMBER
  )

  return {
    props: {
      articles: data,
      currentPage: page,
      pageSize,
      ...(await serverSideTranslations(locale || "en", ["common", "contactUs"]))
    },
    revalidate: 300
  }
}
export const BlogTabData = [
  {
    id: "tag-hot"
  },
  {
    id: "tag-news"
  },
  {
    id: "tag-NFT"
  },
  {
    id: "tag-blockChain"
  },
  {
    id: "tag-DeFi"
  },
  {
    id: "tag-security"
  },
  {
    id: "tag-Compliance"
  },
  {
    id: "tag-update"
  }
] as const

const Blog: React.FC<InferGetServerSidePropsType<typeof getStaticProps>> = ({
  articles,
  currentPage,
  pageSize
}) => {
  const router = useRouter()
  const { locale } = router
  const { t } = useTranslation("common")

  const [localArticles, setLocalArticles] = useState(articles)
  const [didClientFetch, setDidClientFetch] = useState(false)
  const [menuTab, setMenuTab] = useState("All")
  const [blogs, setBlogs] = useState<any[]>(localArticles.data)
  const [sourceBlogs, setSourceBlogs] = useState<any[]>(localArticles.data)

  const blogTab = useCallback(
    (t: string) => {
      let _blogs = [...sourceBlogs]

      const filteredBlogs = _blogs.filter(
        article =>
          article.attributes?.tags?.data.some(
            ({ attributes }: { attributes: any }) =>
              attributes.name.toLowerCase() === t.toLowerCase()
          ) || t === "All"
      )
      setBlogs(filteredBlogs)
    },
    [sourceBlogs]
  )

  const fetchAllBlogs = useCallback(async () => {
    if (didClientFetch) return
    const { data } = await getBlogs(locale, 999, DEFAULT_PAGE_NUMBER)
    setDidClientFetch(true)
    setLocalArticles(data)
  }, [didClientFetch, locale])

  useEffect(() => {
    setLocalArticles(articles)
  }, [articles])

  useEffect(() => {
    BlogTabData.map(({ id: tabName }) => {
      if (router.asPath.includes(`#${tabName}`)) {
        setMenuTab(tabName)
        blogTab(t(tabName))
      }
    })
  }, [blogTab, router.asPath, t])

  function sortBlogsByDate(blogs: any[]) {
    blogs.forEach(blog => {
      if (!blog.attributes.date) {
        blog.attributes.date = blog.createdAt
      }
    })

    blogs.sort(
      (a, b) =>
        new Date(b.attributes.date).getTime() -
        new Date(a.attributes.date).getTime()
    )

    return blogs
  }

  useEffect(() => {
    const blogs = sortBlogsByDate(localArticles.data)
    setBlogs(blogs)
    setSourceBlogs(blogs)
  }, [localArticles.data])

  const search = (t: string) => {
    let _blogs = [...sourceBlogs]

    _blogs = _blogs.filter(p => {
      const matchArray = p.attributes.title.match(new RegExp(t, "gi"))
      return !!matchArray
    })
    setBlogs(_blogs)
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    router.push(`/blog/${page}`)
  }

  const totalPages = Math.ceil(localArticles.meta.pagination.total / pageSize)

  return (
    <>
      <MetaData
        title={t("blog.meta.title")}
        description={t("blog.meta.description")}
        url={t("blog.meta.url")}
        image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
        languageAlternates={[
          {
            hrefLang: "en-US",
            href: "https://www.kryptogo.com/blog"
          },
          {
            hrefLang: "zh-TW",
            href: "https://www.kryptogo.com/tw/blog"
          },
          {
            hrefLang: "zh-CN",
            href: "https://www.kryptogo.com/cn/blog"
          }
        ]}
      />

      <ResetCSS />
      <Sticky top={0} innerZ={1000} activeClass="sticky-active">
        <NavbarNew />
      </Sticky>
      <SectionWrapper>
        <TitleWrapper>
          <SectionHeading title={t("navbar.resources.blog.title")} />
        </TitleWrapper>

        <BlogMenuBar>
          <BlogMenu
            Data={BlogTabData}
            tab={menuTab}
            setTab={setMenuTab}
            search={blogTab}
            onChange={fetchAllBlogs}
          />
          <Search onFocus={fetchAllBlogs} search={search} />
        </BlogMenuBar>

        <ContentWrapper>
          <BlogCardsWrapper>
            {blogs?.map(item => (
              <BlogCard
                key={item.id}
                img={
                  item?.attributes?.cover_image?.data?.attributes?.formats
                    ?.medium?.url ||
                  item?.attributes?.cover_image?.data?.attributes?.url // GIF
                }
                title={item.attributes.title}
                desc={item.attributes.description}
                date={item.attributes.date}
                link={`${locale === "en" ? "" : `/${locale}`}/blog/${
                  item.attributes["blog_identifier"]?.data?.attributes
                    .Identifier
                }`}
                tags={item.attributes?.tags?.data.map(
                  ({ attributes }: { attributes: any }) => attributes.name
                )}
              />
            ))}
          </BlogCardsWrapper>
        </ContentWrapper>

        {!didClientFetch && (
          <PaginationWrapper>
            <Pagination
              count={totalPages}
              page={+currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#fff" // Set the default text color
                },
                "& .Mui-selected": {
                  backgroundColor: "#ffb74d", // Set the background color of the selected item
                  color: "#000" // Set the text color of the selected item
                }
              }}
            />
          </PaginationWrapper>
        )}

        <SectionLine />
      </SectionWrapper>

      <Footer />
    </>
  )
}

export default Blog

const SectionWrapper = styled.div`
  ${section()}
  margin-top: 0px;
`
const TitleWrapper = styled.div`
  padding-top: 160px;
  @media (max-width: 768px) {
    padding-top: 100px;
  }
`
const BlogMenuBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0px;
  margin-bottom: var(--textGap);
  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: end;
    gap: 16px;
  }
`
const BlogCardsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--cardGap);
  @media (${theme.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (${theme.md}) {
    grid-template-columns: repeat(1, 1fr);
  }
`

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`
